import React, { useMemo } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

/**
 * VoucherTypeMap (Iter-194 / Iter-195) — shared mini-picker for Tally & Zoho migrators.
 *
 * Iter-195 additions:
 *  - Live "sanity diff" — count of source vouchers that will land in each
 *    Zenith target based on the CURRENT mapping. Updates instantly on any
 *    Select change so misses show up before commit.
 *  - Optional `persist` chip showing last-saved timestamp.
 */
const ZENITH_TARGETS = [
  { v: "sales", l: "Sales Invoice", color: "bg-emerald-100 text-emerald-800" },
  { v: "purchase", l: "Purchase Bill", color: "bg-amber-100 text-amber-800" },
  { v: "receipt", l: "Receipt", color: "bg-blue-100 text-blue-800" },
  { v: "payment", l: "Payment", color: "bg-rose-100 text-rose-800" },
  { v: "contra", l: "Contra", color: "bg-violet-100 text-violet-800" },
  { v: "journal", l: "Journal (default)", color: "bg-slate-100 text-slate-800" },
  { v: "credit_note", l: "Credit Note", color: "bg-teal-100 text-teal-800" },
  { v: "debit_note", l: "Debit Note", color: "bg-orange-100 text-orange-800" },
];

export default function VoucherTypeMap({ summary, value, onChange, testidPrefix = "vtype", persistedAt }) {
  const rows = Array.isArray(summary) ? summary : [];

  // Iter-195 · Live sanity totals — sum(source-counts) per resolved Zenith target
  // + list of ambiguous rows still landing in "journal" so the accountant can
  //   spot & fix them before commit.
  const diff = useMemo(() => {
    const totals = {};
    let grand = 0;
    const journalRows = [];   // source types resolving to journal ≠ true journals
    for (const r of rows) {
      const src = r.source_type || "(unknown)";
      const tgt = (value || {})[src] || r.default_zenith_type || "journal";
      totals[tgt] = (totals[tgt] || 0) + (r.count || 0);
      grand += (r.count || 0);
      // A row is "suspicious" if it's landing in Journal but its source name
      // hints at Sales/Purchase/Receipt/Payment (accountant probably wants
      // to remap it).
      if (tgt === "journal") {
        const s = src.toLowerCase();
        const hints = ["sales", "sale ", "invoice", "b2b", "b2c", "bill",
                        "purchase", "purch", "receipt", "rcpt", "payment",
                        "pymt", "tax", "gst", "export", "import", "contra"];
        if (hints.some((h) => s.includes(h)) && s !== "journal") {
          journalRows.push({ src, count: r.count || 0 });
        }
      }
    }
    return { totals, grand, journalRows };
  }, [rows, value]);

  if (rows.length === 0) return null;
  const set = (src, tgt) => onChange && onChange({ ...(value || {}), [src]: tgt });

  return (
    <div className="rounded-sm border-2 border-orange-400 bg-orange-50/50 p-4 space-y-3 shadow-sm" data-testid={`${testidPrefix}-wrap`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <Label className="text-[11px] font-mono uppercase tracking-widest text-orange-700 font-bold">
            ⚙︎ Step 1 · Confirm voucher-type mapping
          </Label>
          <div className="text-[12px] text-foreground mt-1 leading-relaxed">
            <b>Please review before committing.</b> Each source voucher type below will be posted into Zenith 360 as the target on the right.
            If you don't map it, unrecognised source types fall into <b>Journal</b> — which is why Sales/Purchase sometimes end up as Journals.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {persistedAt && (
            <span className="text-[10px] font-mono text-emerald-700 bg-emerald-100 rounded-sm px-1.5 py-0.5"
                    data-testid={`${testidPrefix}-persisted`}>
              Auto-loaded from your last import · {new Date(persistedAt).toLocaleDateString()}
            </span>
          )}
          <span className="text-[10px] font-mono text-orange-700/70">{rows.length} type(s)</span>
        </div>
      </div>

      {/* Iter-198 · Red-flag banner for source types landing in Journal that look like Sales/Purchase/etc. */}
      {diff.journalRows.length > 0 && (
        <div className="rounded-sm border-2 border-red-500 bg-red-50 p-3" data-testid={`${testidPrefix}-journal-warning`}>
          <div className="text-[11px] font-mono uppercase tracking-widest text-red-700 font-bold mb-1">
            ⚠︎ {diff.journalRows.reduce((s, r) => s + r.count, 0)} voucher(s) will land in JOURNAL — probably wrong!
          </div>
          <div className="text-[12px] text-red-800 mb-2">
            The following source types look like Sales/Purchase/Receipt/Payment but are currently going into <b>Journal</b>. Please map them to the correct Zenith target in the table below:
          </div>
          <div className="flex flex-wrap gap-1.5">
            {diff.journalRows.map((r) => (
              <span key={r.src} className="text-[11px] rounded-sm px-2 py-0.5 bg-red-200 text-red-900 font-mono">
                {r.src} <span className="font-semibold">×{r.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Iter-195 · Live sanity diff */}
      <div className="rounded-sm border border-orange-200 bg-white p-2.5" data-testid={`${testidPrefix}-diff`}>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
          Preview diff · what lands where ({diff.grand} source voucher{diff.grand === 1 ? "" : "s"})
        </div>
        <div className="flex flex-wrap gap-1.5">
          {ZENITH_TARGETS.map((t) => {
            const c = diff.totals[t.v] || 0;
            const pct = diff.grand ? Math.round((c / diff.grand) * 100) : 0;
            if (c === 0) return null;
            return (
              <div key={t.v} className={`text-[11px] rounded-sm px-2 py-1 ${t.color} flex items-center gap-1.5`}
                    data-testid={`${testidPrefix}-diff-${t.v}`}>
                <span className="font-semibold">{t.l}</span>
                <span className="font-mono tabular-nums">{c}</span>
                <span className="text-[9px] opacity-70">({pct}%)</span>
              </div>
            );
          })}
          {diff.grand === 0 && (
            <span className="text-[11px] text-muted-foreground italic">No mapped rows.</span>
          )}
        </div>
        {/* Proportional stacked bar */}
        {diff.grand > 0 && (
          <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-sm overflow-hidden flex">
            {ZENITH_TARGETS.map((t) => {
              const c = diff.totals[t.v] || 0;
              if (c === 0) return null;
              const pct = (c / diff.grand) * 100;
              const color = {
                sales: "#10b981", purchase: "#f59e0b", receipt: "#3b82f6",
                payment: "#f43f5e", contra: "#8b5cf6", journal: "#64748b",
                credit_note: "#14b8a6", debit_note: "#f97316",
              }[t.v];
              return (
                <div key={t.v} style={{ width: `${pct}%`, backgroundColor: color }} title={`${t.l} ${pct.toFixed(1)}%`} />
              );
            })}
          </div>
        )}
      </div>

      {/* Picker table */}
      <div className="overflow-x-auto rounded-sm border border-orange-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-orange-100/60 sticky top-0">
            <tr className="text-[10px] font-mono uppercase text-orange-800">
              <th className="text-left py-2 px-3 min-w-[220px]">Source type (from your file)</th>
              <th className="text-right py-2 px-3 w-[80px]">Count</th>
              <th className="text-left py-2 px-3 w-[130px]">Auto-suggested</th>
              <th className="text-left py-2 px-3 min-w-[220px]">Zenith target · YOU CONFIRM</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const src = row.source_type || "(unknown)";
              const cur = (value || {})[src] || row.default_zenith_type || "journal";
              const suspect = cur === "journal" && src.toLowerCase() !== "journal";
              return (
                <tr key={src}
                      className={`border-t border-orange-200/60 ${suspect ? "bg-red-50/70" : ""}`}
                      data-testid={`${testidPrefix}-row-${src.replace(/\s+/g, "_")}`}>
                  <td className="py-2 px-3 font-medium break-words">{src}</td>
                  <td className="py-2 px-3 text-right font-mono tabular-nums">{row.count}</td>
                  <td className="py-2 px-3 text-[11px] text-muted-foreground font-mono">
                    {row.default_zenith_type || "journal"}
                  </td>
                  <td className="py-2 px-3">
                    <Select value={cur} onValueChange={(v) => set(src, v)}>
                      <SelectTrigger
                          className={`rounded-sm h-9 text-xs w-full min-w-[200px] ${suspect ? "border-red-500 bg-red-50" : ""}`}
                          data-testid={`${testidPrefix}-target-${src.replace(/\s+/g, "_")}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ZENITH_TARGETS.map((t) => (
                          <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
